import { debounce } from '@sensenet/client-utils';
import React, { createContext, useContext, useEffect, useState } from 'react';
import Semaphore from 'semaphore-async-await';
import { useLogger, useRepository, useRepositoryEvents } from "../hooks/index.js";
import { CurrentContentContext } from "./current-content.js";
/**
 * Context that will return with the current content's ancestors
 */

export const CurrentAncestorsContext = /*#__PURE__*/createContext([]);
CurrentAncestorsContext.displayName = 'CurrentAncestorsContext';

/**
 * Provider component for the CurrentAncestorsContext component.
 * Loads an ancestor list from the Repository. Has to be wrapped with a **CurrentContentContext** and a **RepositoryContext**
 */
export const CurrentAncestorsProvider = props => {
  const currentContent = useContext(CurrentContentContext);
  const [loadLock] = useState(new Semaphore(1));
  const [ancestors, setAncestors] = useState([]);
  const repo = useRepository();
  const eventHub = useRepositoryEvents();
  const [reloadToken, setReloadToken] = useState(1);
  const logger = useLogger('CurrentAncestorsProvider');
  const requestReload = debounce(newId => {
    if (ancestors.map(a => a.Id).includes(newId)) {
      setReloadToken(Math.random());
    }
  }, 100);
  useEffect(() => {
    const subscriptions = [eventHub.onContentModified.subscribe(mod => {
      requestReload(mod.content.Id);
    }), eventHub.onContentMoved.subscribe(moved => {
      moved.content.forEach(movedContent => requestReload(movedContent.Id));
    }), eventHub.onContentDeleted.subscribe(del => {
      del.contentData.forEach(deletedContent => requestReload(deletedContent.Id));
    })];
    return () => subscriptions.forEach(s => s.dispose());
  }, [ancestors, eventHub.onContentDeleted, eventHub.onContentModified, eventHub.onContentMoved, repo.configuration.repositoryUrl, requestReload]);
  const [error, setError] = useState();
  useEffect(() => {
    const ac = new AbortController();

    (async () => {
      try {
        await loadLock.acquire();

        if (props.root && currentContent.Id === props.root || currentContent.Path === props.root) {
          setAncestors([]);
        } else if (currentContent.Id) {
          const ancestorsResult = await repo.executeAction({
            idOrPath: currentContent.Id,
            method: 'GET',
            name: 'Ancestors',
            body: undefined,
            requestInit: {
              signal: ac.signal
            },
            oDataOptions: {
              orderby: [['Path', 'asc']]
            }
          });
          const rootIndex = ancestorsResult.d.results.findIndex(a => a.Id === props.root || a.Path === props.root);
          setAncestors(rootIndex > 0 ? ancestorsResult.d.results.slice(rootIndex) : ancestorsResult.d.results);
        }
      } catch (err) {
        if (!ac.signal.aborted) {
          setError(err);
        }
      } finally {
        loadLock.release();
      }
    })();

    return () => ac.abort();
  }, [currentContent.Id, currentContent.Path, loadLock, props.root, reloadToken, repo]);

  if (error) {
    logger.warning({
      message: `Error loading ancestors. ${error.message}`,
      data: {
        error,
        relatedContent: currentContent,
        relatedRepository: repo.configuration.repositoryUrl
      }
    });
  }

  return /*#__PURE__*/React.createElement(CurrentAncestorsContext.Provider, {
    value: ancestors
  }, props.children);
};
//# sourceMappingURL=current-ancestors.js.map